import React, { useState } from 'react';
import { TestTube, Play } from 'lucide-react';
import { Button, toast } from '@ncoder/ui';
import { AgentTestingContent } from '@/components/organisms/testing/AgentTestingContent';

interface TestResult {
  id: number;
  scenario: string;
  status: 'passed' | 'failed';
  response: string;
  score: number;
  timestamp: string;
}

interface TestCategory {
  id: string;
  label: string;
  status: 'passed' | 'needs-work';
}

export const AgentTesting: React.FC = () => {
  const [testMessage, setTestMessage] = useState('');
  const [isRunning, setIsRunning] = useState(false);

  const [testResults] = useState<TestResult[]>([
    {
      id: 1,
      scenario: 'Customer Support Query',
      status: 'passed',
      response: "I'd be happy to help you with your billing question...",
      score: 94,
      timestamp: '2 minutes ago',
    },
    {
      id: 2,
      scenario: 'Product Information Request',
      status: 'passed',
      response: 'Our premium plan includes advanced features...',
      score: 89,
      timestamp: '5 minutes ago',
    },
    {
      id: 3,
      scenario: 'Complex Technical Issue',
      status: 'failed',
      response: "I'm not sure how to handle this request...",
      score: 62,
      timestamp: '8 minutes ago',
    },
  ]);

  const [categories] = useState<TestCategory[]>([
    { id: 'basic', label: 'Basic Functionality', status: 'passed' },
    { id: 'context', label: 'Context Understanding', status: 'passed' },
    { id: 'error', label: 'Error Handling', status: 'needs-work' },
    { id: 'performance', label: 'Performance', status: 'passed' },
  ]);

  const handleSendTest = () => {
    setIsRunning(true);
    toast({
      title: 'Test Running',
      description: 'Sending your test message to the agent...',
    });
    // Simulate test completion
    setTimeout(() => {
      setIsRunning(false);
      setTestMessage('');
    }, 2000);
  };

  const handleScenarioClick = (scenario: string) => {
    toast({
      title: 'Running Scenario',
      description: `Testing ${scenario} scenarios...`,
    });
  };

  const handleRunAllTests = () => {
    toast({
      title: 'Running All Tests',
      description: 'Executing complete test suite...',
    });
  };

  const handleRunQuickTest = () => {
    toast({
      title: 'Quick Test',
      description: 'Running quick validation test...',
    });
  };

  const handleViewAnalytics = () => {
    toast({
      title: 'Analytics',
      description: 'Opening analytics dashboard...',
    });
  };

  const handleCustomTestSuite = () => {
    toast({
      title: 'Custom Test Suite',
      description: 'Creating custom test suite...',
    });
  };

  return (
    <div className="w-full max-w-none space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-page-title flex items-center gap-2">
            <TestTube className="h-8 w-8 text-primary" />
            Agent Testing
          </h1>
          <p className="text-subtitle">Test and validate your agent's performance</p>
        </div>
        <Button size="sm" disabled={isRunning} onClick={handleRunAllTests}>
          <Play className="h-4 w-4 mr-2" />
          {isRunning ? 'Running Tests...' : 'Run All Tests'}
        </Button>
      </div>

      {/* Main Content */}
      <AgentTestingContent
        testMessage={testMessage}
        isRunning={isRunning}
        onMessageChange={setTestMessage}
        onSendTest={handleSendTest}
        onScenarioClick={handleScenarioClick}
        testResults={testResults}
        successRate="85%"
        avgResponseTime="1.2s"
        userSatisfaction="92%"
        testsRun="147"
        testsPassed={124}
        testsFailed={23}
        statusSuccessRate="84.3%"
        categories={categories}
        onRunQuickTest={handleRunQuickTest}
        onViewAnalytics={handleViewAnalytics}
        onCustomTestSuite={handleCustomTestSuite}
      />
    </div>
  );
};

export default AgentTesting;